import React, { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import NewButton from '../../components/NewButton/NewButton';

// Mock data for demonstration
const mockAddresses = [
    {
        id: '1',
        ErpCustomerNumber: 'CUST0001',
        BranchName: 'Main Branch',
        City: 'Istanbul',
        Province: 'Istanbul',
        Country: 'Turkey',
        BranchStatus: 'Active'
    },
    {
        id: '2',
        ErpCustomerNumber: 'CUST0002',
        BranchName: 'East Branch',
        City: 'Ankara',
        Province: 'Ankara',
        Country: 'Turkey',
        BranchStatus: 'Inactive'
    },
    {
        id: '3',
        ErpCustomerNumber: 'CUST0003',
        BranchName: 'South Branch',
        City: 'Izmir',
        Province: 'Izmir',
        Country: 'Turkey',
        BranchStatus: 'Active'
    }
];

const statusOptions = [
    { name: 'Active', code: 'Active' },
    { name: 'Inactive', code: 'Inactive' }
];

const provinceOptions = [
    { name: 'Istanbul', code: 'Istanbul' },
    { name: 'Ankara', code: 'Ankara' },
    { name: 'Izmir', code: 'Izmir' },
    { name: 'Bursa', code: 'Bursa' },
    { name: 'Antalya', code: 'Antalya' }
];

const countryOptions = [
    { name: 'Turkey', code: 'Turkey' },
    { name: 'Germany', code: 'Germany' },
    { name: 'France', code: 'France' },
    { name: 'United Kingdom', code: 'United Kingdom' },
    { name: 'United States', code: 'United States' }
];

const CustomerAddressReportPage = () => {
    const [addresses, setAddresses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        ErpCustomerNumber: {
            operator: FilterOperator.AND,
            constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }]
        },
        BranchName: {
            operator: FilterOperator.AND,
            constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }]
        },
        City: { value: null, matchMode: FilterMatchMode.EQUALS },
        Province: { value: null, matchMode: FilterMatchMode.EQUALS },
        Country: { value: null, matchMode: FilterMatchMode.EQUALS },
        BranchStatus: { value: null, matchMode: FilterMatchMode.EQUALS }
    });
    const [displayModal, setDisplayModal] = useState(false);
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [formData, setFormData] = useState({
        ErpCustomerNumber: '',
        BranchName: '',
        City: '',
        Province: '',
        Country: '',
        BranchStatus: ''
    });

    useEffect(() => {
        setAddresses(mockAddresses);
        setLoading(false);
    }, []);

    useEffect(() => {
        if (selectedAddress) {
            setFormData({
                ErpCustomerNumber: selectedAddress.ErpCustomerNumber,
                BranchName: selectedAddress.BranchName,
                City: selectedAddress.City,
                Province: selectedAddress.Province,
                Country: selectedAddress.Country,
                BranchStatus: selectedAddress.BranchStatus
            });
        } else {
            setFormData({
                ErpCustomerNumber: '',
                BranchName: '',
                City: '',
                Province: '',
                Country: '',
                BranchStatus: ''
            });
        }
    }, [selectedAddress]);

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };

        _filters['global'].value = value;

        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const handleNew = () => {
        setSelectedAddress(null);
        setDisplayModal(true);
    };

    const handleEdit = (address) => {
        setSelectedAddress(address);
        setDisplayModal(true);
    };

    const handleSave = () => {
        // Validation
        if (!formData.ErpCustomerNumber || !formData.BranchName) {
            return; // Basic validation
        }

        if (selectedAddress) {
            // Update existing address
            const updatedAddresses = addresses.map((address) =>
                address.id === selectedAddress.id
                    ? {
                          ...address,
                          ErpCustomerNumber: formData.ErpCustomerNumber,
                          BranchName: formData.BranchName,
                          City: formData.City,
                          Province: formData.Province,
                          Country: formData.Country,
                          BranchStatus: formData.BranchStatus
                      }
                    : address
            );
            setAddresses(updatedAddresses);
        } else {
            // Add new address
            const newAddress = {
                id: Math.floor(Math.random() * 10000).toString(),
                ErpCustomerNumber: formData.ErpCustomerNumber,
                BranchName: formData.BranchName,
                City: formData.City,
                Province: formData.Province,
                Country: formData.Country,
                BranchStatus: formData.BranchStatus
            };
            setAddresses([...addresses, newAddress]);
        }

        setDisplayModal(false);
    };

    const handleRemove = () => {
        if (selectedAddress) {
            const filteredAddresses = addresses.filter((address) => address.id !== selectedAddress.id);
            setAddresses(filteredAddresses);
            setDisplayModal(false);
        }
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="flex gap-2">
                <i onClick={() => handleEdit(rowData)} className="pi pi-pencil" style={{ left: '1.5rem', color: '#0B3187' }}></i>
            </div>
        );
    };

    const statusBodyTemplate = (rowData) => {
        const status = rowData.BranchStatus;
        let severity = status === 'Active' ? 'success' : 'warning';

        return <span className={`p-badge p-badge-${severity}`}>{status}</span>;
    };

    const header = (
        <div className="flex justify-content-between align-items-center">
            <div className="flex align-items-center">
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Ara..." />
                </span>
            </div>
            <div className="flex gap-2">
            <NewButton onClick={handleNew} />
            </div>
        </div>
    );

    const footerContent = (
        <div className="flex justify-content-end gap-2">
            {selectedAddress && <Button label="Sil" icon="pi pi-trash" severity="danger" onClick={handleRemove} />}
            <Button label="İptal" icon="pi pi-times" outlined onClick={() => setDisplayModal(false)} />
            <Button label="Kaydet" icon="pi pi-check" onClick={handleSave} />
        </div>
    );

    return (
        <div className="card">
            <div className="flex justify-content-between align-items-center mb-4">
                <h1 className="m-0">Müşteri Adres Raporu</h1>
            </div>

            <Card>
                <DataTable
                    value={addresses}
                    paginator
                    className="p-datatable-gridlines"
                    showGridlines
                    rows={10}
                    dataKey="id"
                    loading={loading}
                    filters={filters}
                    filterDisplay="menu"
                    globalFilterFields={['ErpCustomerNumber', 'BranchName', 'City', 'Province', 'Country']}
                    header={header}
                    emptyMessage="Müşteri adresi bulunamadı."
                    rowsPerPageOptions={[5, 10, 25, 50, 100, 500, 1000]}
                    tableStyle={{ minWidth: '50rem' }}
                >
                    <Column sortable bodyClassName="text-center" headerClassName="text-center" body={actionBodyTemplate} exportable={false} style={{ width: '5%', minWidth: '33px' }}></Column>
                    <Column field="ErpCustomerNumber" header="Müşteri Numarası" filter filterPlaceholder="Müşteri numarası ara..." sortable style={{ width: '20%' }} />
                    <Column field="BranchName" header="Şube Adı" filter filterPlaceholder="Şube adı ara..." sortable style={{ width: '20%' }} />
                    <Column field="City" header="Şehir" filter filterPlaceholder="Şehir ara..." sortable style={{ width: '15%' }} />
                    <Column
                        field="Province"
                        header="İl"
                        filter
                        filterElement={(options) => <Dropdown value={options.value} options={provinceOptions} onChange={(e) => options.filterCallback(e.value)} optionLabel="name" optionValue="code" placeholder="Tümü" className="p-column-filter" />}
                        sortable
                        style={{ width: '15%' }}
                    />
                    <Column
                        field="Country"
                        header="Ülke"
                        filter
                        filterElement={(options) => <Dropdown value={options.value} options={countryOptions} onChange={(e) => options.filterCallback(e.value)} optionLabel="name" optionValue="code" placeholder="Tümü" className="p-column-filter" />}
                        sortable
                        style={{ width: '15%' }}
                    />
                    <Column
                        field="BranchStatus"
                        header="Durum"
                        body={statusBodyTemplate}
                        filter
                        filterElement={(options) => <Dropdown value={options.value} options={statusOptions} onChange={(e) => options.filterCallback(e.value)} optionLabel="name" optionValue="code" placeholder="Tümü" className="p-column-filter" />}
                        sortable
                        style={{ width: '10%' }}
                    />
                </DataTable>
            </Card>

            {/* Edit/Create Modal */}
            <Dialog header={selectedAddress ? 'Müşteri Adresi Düzenle' : 'Yeni Müşteri Adresi'} visible={displayModal} style={{ width: '50%' }} modal footer={footerContent} onHide={() => setDisplayModal(false)}>
                <div className="p-fluid">
                    <div className="field mb-4">
                        <label htmlFor="ErpCustomerNumber" className="font-bold block mb-2">
                            Müşteri Numarası*
                        </label>
                        <InputText id="ErpCustomerNumber" value={formData.ErpCustomerNumber} onChange={(e) => setFormData({ ...formData, ErpCustomerNumber: e.target.value })} required />
                    </div>

                    <div className="field mb-4">
                        <label htmlFor="BranchName" className="font-bold block mb-2">
                            Şube Adı*
                        </label>
                        <InputText id="BranchName" value={formData.BranchName} onChange={(e) => setFormData({ ...formData, BranchName: e.target.value })} required />
                    </div>

                    <div className="field mb-4">
                        <label htmlFor="City" className="font-bold block mb-2">
                            Şehir
                        </label>
                        <InputText id="City" value={formData.City} onChange={(e) => setFormData({ ...formData, City: e.target.value })} />
                    </div>

                    <div className="field mb-4">
                        <label htmlFor="Province" className="font-bold block mb-2">
                            İl
                        </label>
                        <Dropdown id="Province" value={formData.Province} options={provinceOptions.map((p) => p.name)} onChange={(e) => setFormData({ ...formData, Province: e.value })} placeholder="İl Seçiniz" />
                    </div>

                    <div className="field mb-4">
                        <label htmlFor="Country" className="font-bold block mb-2">
                            Ülke
                        </label>
                        <Dropdown id="Country" value={formData.Country} options={countryOptions.map((c) => c.name)} onChange={(e) => setFormData({ ...formData, Country: e.value })} placeholder="Ülke Seçiniz" />
                    </div>

                    <div className="field mb-4">
                        <label htmlFor="BranchStatus" className="font-bold block mb-2">
                            Durum
                        </label>
                        <Dropdown id="BranchStatus" value={formData.BranchStatus} options={statusOptions.map((s) => s.name)} onChange={(e) => setFormData({ ...formData, BranchStatus: e.value })} placeholder="Durum Seçiniz" />
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default CustomerAddressReportPage;
